import React, { useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../store/hooks";
import { TextComponent as TextComponent_ } from "./interfaces/base";
import styled from "@emotion/styled";
import RenderChildren from "./RenderChildren";
import BackgroundVideoComponent from "./BackgroundVideoComponent";
import useLazyLoad from "./utilities/useLazyLoad";
import { EditorContent } from "@tiptap/react";
import {
  getBuilderDomId,
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import { useInlineComponentsMap } from "./context/InlineComponentsMapContext";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

interface Props {
  id: number;
  uuid?: string | null;
  attributes: TextComponent_["attributes"];
  editor: JSX.Element;
  childElements: any;
  childWebpageComponentRelations: any;
}

export default function TextComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: Props) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const typographies = useAppSelector((state) => state.builder.typographies);
  const colors = useAppSelector((state) => state.builder.colors);
  const [style, setStyle] = useState<React.CSSProperties>();
  const isBuilder = document.getElementById("BuilderContent");
  const inlineComponentsMap = useInlineComponentsMap();
  const structureComponent = useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as any;
  const componentIdentity =
    uuid?.trim() || (id != null ? String(id) : "");
  const inlineComponent =
    !structureComponent &&
    inlineComponentsMap &&
    typeof inlineComponentsMap === "object"
      ? inlineComponentsMap[componentIdentity] ?? inlineComponentsMap[String(id)]
      : null;
  const component =
    structureComponent ??
    inlineComponent ??
    ({
      id,
      uuid: uuid ?? null,
      attributes,
      childElements: childElements ?? [],
      childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    } as any);
  const resolvedAttributes =
    component?.attributes && typeof component.attributes === "object"
      ? component.attributes
      : attributes && typeof attributes === "object"
      ? attributes
      : {};
  const resolvedStyles =
    resolvedAttributes?.styles && typeof resolvedAttributes.styles === "object"
      ? resolvedAttributes.styles
      : {};
  const isEditing = matchesEditorTarget(component, editorId);
  const domId =
    getBuilderDomId({ id, uuid }) ||
    getBuilderDomId({ id: component?.id, uuid: component?.uuid });

  const [templateEditText, setTemplateEditText] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(
    resolvedStyles.backgroundImage ? resolvedStyles.backgroundImage : ""
  );

  const builderEditor = useAppSelector((state) => state.builder.editor);

  const textElementRef = useRef<HTMLButtonElement>(null);
  const loaded = useLazyLoad(textElementRef, backgroundImage);
  const [backgroundStyles, setBackgroundStyles] =
    useState<React.CSSProperties>();
  const [borderColors, setBorderColors] = useState<any>();

  useEffect(() => {
    if (resolvedStyles.backgroundImage) {
      setBackgroundImage(resolvedStyles.backgroundImage);
    }
  }, [resolvedStyles.backgroundImage]);

  let editor = useAppSelector((state) => state.builder?.editor?.editor);

  useEffect(() => {
    const compTypography = typographies?.find(
      (x: any) => x.id === resolvedAttributes.typography_id
    );

    if (compTypography) {
      setStyle({
        fontSize: compTypography.fontSize + compTypography.fontSizeUnit,
        fontFamily: compTypography.fontFamily,
        fontStyle: compTypography.fontStyle,
        fontWeight: compTypography.fontWeight,
        letterSpacing: compTypography.letterSpacing,
        lineHeight: compTypography.lineHeight,
        color: colors?.find((x: any) => x.id === compTypography.colorId)
          ?.color,
      });
    }
  }, [typographies, resolvedAttributes]);

  useEffect(() => {
    const backgroundColor = component.attributes?.color_id
      ? colors?.find((x: any) => x.id === component.attributes?.color_id)?.color
      : component.attributes?.styles.backgroundColor
      ? component.attributes?.styles.backgroundColor
      : "";

    const backgroundColorHover = component.attributes?.hover_color_id
      ? colors?.find((x: any) => x.id === component.attributes?.hover_color_id)
          ?.color
      : component.attributes?.styles[":hover"] &&
        component.attributes?.styles[":hover"].backgroundColor
      ? component.attributes?.styles[":hover"].backgroundColor
      : "";

    const backgroundObject = {
      backgroundColor: `${backgroundColor ? backgroundColor : "none"} ${
        backgroundColor ? "!important" : ""
      }`,
      ":hover": {
        backgroundColor: `${backgroundColorHover} ${
          backgroundColor ? "!important" : ""
        }`,
      },
    };

    setBackgroundStyles(backgroundObject);
  }, [
    resolvedAttributes,
    component.attributes?.color_id,
    component.attributes?.hover_color_id,
    component.attributes?.styles.backgroundColor,
    component.attributes?.styles[":hover"]?.backgroundColor,
    colors,
  ]);

  useEffect(() => {
    // Change border color values

    const borderColorValues = {
      borderTopColor: component.attributes?.borderTopColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderTopColorId
          )?.color
        : component.attributes?.styles.borderTopColor
        ? component.attributes?.styles.borderTopColor
        : "",
      borderBottomColor: component.attributes?.borderBottomColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderBottomColorId
          )?.color
        : component.attributes?.styles.borderBottomColor
        ? component.attributes?.styles.borderBottomColor
        : "",
      borderLeftColor: component.attributes?.borderLeftColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderLeftColorId
          )?.color
        : component.attributes?.styles.borderLeftColor
        ? component.attributes?.styles.borderLeftColor
        : "",
      borderRightColor: component.attributes?.borderRightColorId
        ? colors?.find(
            (x: any) => x.id === component.attributes?.borderRightColorId
          )?.color
        : component.attributes?.styles.borderRightColor,
    };

    setBorderColors(borderColorValues);
  }, [
    resolvedAttributes,
    resolvedStyles,
    component.attributes?.borderBottomColorId,
    component.attributes?.borderLeftColorId,
    component.attributes?.borderRightColorId,
    component.attributes?.borderTopColorId,
    colors,
  ]);

  useEffect(() => {
    const templateContainer = document.getElementById(
      "template_edit_over_lay_content"
    );
    const textElement = textElementRef.current;

    if (
      textElement &&
      templateContainer &&
      templateContainer.contains(textElement)
    ) {
      setTemplateEditText(true);
    }
  }, [editor]);

  const content = (
    <span
      dangerouslySetInnerHTML={{ __html: resolvedAttributes.text }}
      contentEditable={isEditing}
      // onMouseUp={(e) => setMouseUp(e)}
      // onKeyDown={(e) => setKeyDown(e)}
      // onBlur={(e) => setOnBlur(e)}
    ></span>
  );

  const tags: { [key: string]: JSX.Element } = {
    h1: (
      <h1
        style={{
          ...style,
          ...resolvedStyles,

          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h1>
    ),
    h2: (
      <h2
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h2>
    ),
    h3: (
      <h3
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h3>
    ),
    h4: (
      <h4
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h4>
    ),
    h5: (
      <h5
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h5>
    ),
    h6: (
      <h6
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: !isBuilder
            ? loaded
              ? loaded.map((src) => `url("${src}")`).join(", ")
              : "none"
            : resolvedStyles.backgroundImage,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </h6>
    ),
    p: (
      <p
        style={{
          ...style,
          ...resolvedStyles,
          backgroundImage: loaded
            ? loaded.map((src) => `url("${src}")`).join(", ")
            : "none",
          ...backgroundStyles,
          borderLeftColor: borderColors?.borderLeftColor,
          borderRightColor: borderColors?.borderRightColor,
          borderTopColor: borderColors?.borderTopColor,
          borderBottomColor: borderColors?.borderBottomColor,
        }}
      >
        {content}
      </p>
    ),
  };

  return (
    <Div
      ref={textElementRef}
      id={domId}
      className={
        (resolvedAttributes.classes ?? "") +
        " builder-component relative builder-component__text-editor" +
        (isEditing ? " builder-component--active" : "")
      }
      data-style={{
        ...backgroundStyles,
      }}
      spellCheck="false"
      style={{
        position: "relative",
        ...backgroundStyles,
        backgroundColor: backgroundStyles?.backgroundColor
          ? backgroundStyles?.backgroundColor
          : "none",
      }}
    >
      <div style={{ position: "relative", overflow: "hidden" }}>
        <BackgroundVideoComponent componentKey={uuid} />
      </div>
      {!isBuilder && (
        // <div style={style} dangerouslySetInnerHTML={{ __html: content }}></div>
        <>
          {
            tags[
              typographies?.find((x: any) => x.id === resolvedAttributes.typography_id)
                ?.tag ?? "p"
            ]
          }
        </>
      )}
      {!isEditing && isBuilder && (
        // <div style={style} dangerouslySetInnerHTML={{ __html: content }}></div>
        <>
          {
            tags[
              typographies?.find((x: any) => x.id === resolvedAttributes.typography_id)
                ?.tag ?? "p"
            ]
          }
        </>
      )}
      <RenderChildren
        key={domId + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />
      {isBuilder &&
        isEditing &&
        editor &&
        builderEditor &&
        !builderEditor.template && (
          <>
            <div
              className="builder-component_text-data"
              style={{
                ...style,
                ...resolvedStyles,
                backgroundImage: !isBuilder
                  ? loaded
                    ? loaded.map((src) => `url("${src}")`).join(", ")
                    : "none"
                  : resolvedStyles.backgroundImage,
                ...backgroundStyles,
              }}
            >
              <EditorContent
                id={domId + "_content"}
                content={resolvedAttributes.text}
                editor={editor}
              ></EditorContent>
            </div>
          </>
        )}

      {isBuilder &&
        isEditing &&
        editor &&
        templateEditText &&
        builderEditor &&
        builderEditor.template && (
          <>
            <div
              className="builder-component_text-data"
              style={{
                ...style,
                ...resolvedStyles,
                backgroundImage: !isBuilder
                  ? loaded
                    ? loaded.map((src) => `url("${src}")`).join(", ")
                    : "none"
                  : resolvedStyles.backgroundImage,
                ...backgroundStyles,
              }}
            >
              <EditorContent
                id={domId + "_content"}
                content={resolvedAttributes.text}
                editor={editor}
              ></EditorContent>
            </div>
          </>
        )}
    </Div>
  );
}
